/**
 * `hashState()` — the determinism check that everything else leans on (PRD N2).
 *
 * The hash is taken over the RAW BITS of every value, never over a rounded or
 * formatted version: the whole point is to catch a difference in the last bit, which
 * is exactly what rounding would hide.
 *
 * Only exact integer operations are used — `^`, `>>>` and `Math.imul`, which the spec
 * defines exactly — so hashing itself can never be a source of divergence.
 *
 * Two values are normalised before hashing:
 *   • **−0 → +0.** They compare equal and are physically the same state, but their bit
 *     patterns differ, so a raw-bit hash would call two identical tables different.
 *   • **NaN → one canonical pattern.** NaN payloads are not specified, so an engine is
 *     free to produce a different one. A NaN in the state is a bug (there is an
 *     invariant test for it); this only stops it presenting as a *parity* failure.
 */
import type { TableState } from './state.ts';

/* Allocated once at module load, never resized. Reused so hashing allocates nothing. */
const scratch = new Float64Array(1);
const bits = new Uint32Array(scratch.buffer);

const FNV_PRIME = 16777619;
const FNV_OFFSET = 2166136261;

function mix(hash: number, value: number): number {
  return Math.imul(hash ^ value, FNV_PRIME) >>> 0;
}

function mixDouble(hash: number, value: number): number {
  if (value !== value) {
    // Canonical quiet NaN, chosen by us rather than by the engine.
    return mix(mix(hash, 0x7ff80000), 0);
  }
  scratch[0] = value === 0 ? 0 : value;
  return mix(mix(hash, bits[0] ?? 0), bits[1] ?? 0);
}

/**
 * A hex digest of the full ball state. Identical input must give an identical digest
 * in Node, in a Worker and in a browser — proven by the PHY-006 parity test.
 */
export function hashState(state: TableState): string {
  let hash = mix(FNV_OFFSET, state.count);
  for (let i = 0; i < state.count; i++) {
    hash = mixDouble(hash, state.xM[i] ?? 0);
    hash = mixDouble(hash, state.yM[i] ?? 0);
    hash = mixDouble(hash, state.vxMps[i] ?? 0);
    hash = mixDouble(hash, state.vyMps[i] ?? 0);
    hash = mixDouble(hash, state.wxRadS[i] ?? 0);
    hash = mixDouble(hash, state.wyRadS[i] ?? 0);
    hash = mixDouble(hash, state.wzRadS[i] ?? 0);
    hash = mix(hash, state.potted[i] ?? 0);
  }
  return hash.toString(16).padStart(8, '0');
}
